import { NetworkError, StorageError } from './errorHandler';
import { ApiError, apiRequest } from './apiClient';
import type { StorageAdapter, StorageMetadata, StorageQueryParams, StorageQueryResult } from './storageAdapter';

const PUBLIC_RECIPES_KEY = 'foodbook:public:recipes';
const PUBLIC_INGREDIENTS_KEY = 'foodbook:public:ingredients';

function getCatalogType(key: string): 'recipes' | 'ingredients' | null {
  if (key === PUBLIC_RECIPES_KEY) {
    return 'recipes';
  }
  if (key === PUBLIC_INGREDIENTS_KEY) {
    return 'ingredients';
  }
  return null;
}

export class HttpStorageAdapter implements StorageAdapter {
  private toError(error: unknown, operation: 'get' | 'set' | 'delete' | 'query' | 'other'): Error {
    if (error instanceof ApiError && error.status === 401) {
      return new StorageError(`自托管存储操作失败: ${operation}`, operation, {
        originalError: error,
        status: 401,
        unauthorized: true,
      });
    }

    const message = error instanceof Error ? error.message : String(error);
    const normalized = message.toLowerCase();
    if (normalized.includes('fetch') || normalized.includes('network') || normalized.includes('failed')) {
      return new NetworkError('自托管服务连接失败', operation, { originalError: error });
    }
    return new StorageError(`自托管存储操作失败: ${operation}`, operation, { originalError: error });
  }

  async get<T>(key: string): Promise<T | null> {
    try {
      const catalogType = getCatalogType(key);
      if (catalogType) {
        try {
          const response = await apiRequest<{ items: T[] }>(`/api/catalog/${catalogType}`);
          return (response.items ?? []) as T;
        } catch (error) {
          if (!(error instanceof ApiError && error.status === 404)) {
            throw error;
          }
        }
      }

      const response = await apiRequest<{ value: T | null }>(`/api/storage/item?key=${encodeURIComponent(key)}`);
      return response.value ?? null;
    } catch (error) {
      throw this.toError(error, 'get');
    }
  }

  async set<T>(key: string, value: T): Promise<void> {
    try {
      await apiRequest('/api/storage/item', {
        method: 'PUT',
        body: JSON.stringify({ key, value }),
      });
    } catch (error) {
      throw this.toError(error, 'set');
    }
  }

  async delete(key: string): Promise<void> {
    try {
      await apiRequest('/api/storage/item', {
        method: 'DELETE',
        body: JSON.stringify({ key }),
      });
    } catch (error) {
      throw this.toError(error, 'delete');
    }
  }

  async getMany<T>(keys: string[]): Promise<Map<string, T | null>> {
    const entries = await Promise.all(keys.map(async (key) => [key, await this.get<T>(key)] as const));
    return new Map(entries);
  }

  async setMany<T>(entries: Map<string, T>): Promise<void> {
    for (const [key, value] of entries) {
      await this.set(key, value);
    }
  }

  async listKeys(): Promise<string[]> {
    return [];
  }

  async getMetadata(): Promise<StorageMetadata> {
    return {
      adapter: 'http-api',
      lastSync: new Date().toISOString(),
      version: '1.0.0',
    };
  }

  async clear(): Promise<void> {
    throw new StorageError('自托管存储不支持清空所有数据', 'other');
  }

  async addRecord<T>(key: string, record: any): Promise<T> {
    const items = await this.get<any[]>(key) || [];
    const newRecord = { ...record, id: record.id || `srv-${Date.now()}` };
    items.push(newRecord);
    await this.set(key, items);
    return newRecord as T;
  }

  async updateRecord<T>(key: string, id: string, updates: Partial<T>): Promise<void> {
    const items = await this.get<any[]>(key) || [];
    const updated = items.map((item) => item.id === id ? { ...item, ...updates } : item);
    await this.set(key, updated);
  }

  async deleteRecord(key: string, id: string): Promise<void> {
    const items = await this.get<any[]>(key) || [];
    await this.set(key, items.filter((item) => item.id !== id));
  }

  async findRecipesByIngredient(ingredientId: string): Promise<any[]> {
    const publicRecipes = await this.get<any[]>(PUBLIC_RECIPES_KEY) || [];
    return publicRecipes.filter((recipe) =>
      recipe.ingredients?.some((ingredient: any) => ingredient.linkedIngredientId === ingredientId)
    );
  }

  async query<T>(key: string, params?: StorageQueryParams): Promise<StorageQueryResult<T>> {
    try {
      const catalogType = getCatalogType(key);
      if (catalogType) {
        try {
          return await apiRequest<StorageQueryResult<T>>('/api/catalog/query', {
            method: 'POST',
            body: JSON.stringify({ type: catalogType, params }),
          });
        } catch (error) {
          if (!(error instanceof ApiError && error.status === 404)) {
            throw error;
          }
        }
      }

      return await apiRequest<StorageQueryResult<T>>('/api/storage/query', {
        method: 'POST',
        body: JSON.stringify({ key, params }),
      });
    } catch (error) {
      throw this.toError(error, 'query');
    }
  }
}
